import { clearBrandLogo } from "@/lib/brandLogo";

/**
 * Strict branding isolation.
 *
 * A tenant's colours (organization / campus / institution) belong to that tenant's own panel and nowhere else. The
 * platform panels (Super Admin, login, landing, blog, …) always use the fixed Edu Manage Plus brand from index.css,
 * and one institution's colours must never show up for another.
 */

/** Feature flags for how tenant branding may be applied. Flip one here instead of hunting through the code. */
export const BRANDING_FLAGS = {
  /** Master switch. When false no tenant colours are applied anywhere — every panel keeps the platform brand. */
  TENANT_BRANDING_ENABLED: true,
  /**
   * When true (keep it true), tenant colours are applied only on routes of the matching scope, are remembered per scope
   * and per tenant, and are wiped whenever the user lands in a different scope (Super Admin, login, logout, …).
   */
  STRICT_SCOPE_ISOLATION: true,
} as const;

/** PLATFORM = fixed platform brand. PUBLIC_TENANT = a public page for one tenant (e.g. an admission form): shown, never remembered. */
export type BrandingScope = "PLATFORM" | "ORGANIZATION" | "CAMPUS" | "INSTITUTION" | "PUBLIC_TENANT";

/** Scopes whose colours may be remembered between page loads (replayed before first paint by index.html). */
export const PERSISTENT_SCOPES: readonly BrandingScope[] = ["ORGANIZATION", "CAMPUS", "INSTITUTION"];

/** Keep in sync with the inline script in index.html, which cannot import this file. */
export function scopeForPath(pathname: string): BrandingScope {
  if (pathname.startsWith("/institution-admin") || pathname.startsWith("/staff/")) return "INSTITUTION";
  if (pathname.startsWith("/campus-admin")) return "CAMPUS";
  if (pathname.startsWith("/org-admin")) return "ORGANIZATION";
  if (pathname.startsWith("/apply/") || pathname.startsWith("/visit/")) return "PUBLIC_TENANT";
  return "PLATFORM";
}

/** localStorage key read by index.html before first paint. Value: StoredBranding. */
export const BRANDING_VARS_KEY = "branding:cssVars";

export interface StoredBranding {
  scope: BrandingScope;
  /** e.g. "institution:7" — the one tenant these colours belong to. */
  tenantKey: string;
  vars: Record<string, string>;
}

/** Every CSS variable tenant branding can set. */
export const BRAND_VAR_KEYS = [
  "--brand-navy",
  "--dashboard-text-dark",
  "--brand-neutral-50-rgb",
  "--brand-neutral-100-rgb",
  "--brand-neutral-200-rgb",
  "--brand-neutral-300-rgb",
  "--dashboard-app-bg",
  "--brand-orange",
  "--brand-orange-50",
  "--brand-orange-100",
  "--brand-orange-200",
  "--brand-orange-300",
  "--brand-orange-400",
  "--brand-orange-500",
  "--brand-orange-600",
  "--brand-orange-700",
  "--brand-orange-800",
  "--brand-orange-900",
  "--primary",
  "--primary-foreground",
  "--ring",
] as const;

/** Whether tenant colours of `declared` scope may be applied on `pathname`. */
export function isBrandingAllowed(declared: BrandingScope, pathname: string): boolean {
  if (!BRANDING_FLAGS.TENANT_BRANDING_ENABLED) return false;
  if (declared === "PLATFORM") return false;
  if (!BRANDING_FLAGS.STRICT_SCOPE_ISOLATION) return true;
  return scopeForPath(pathname) === declared;
}

export function readStoredBranding(): StoredBranding | null {
  try {
    const raw = JSON.parse(localStorage.getItem(BRANDING_VARS_KEY) || "null");
    if (raw && typeof raw === "object" && raw.scope && raw.tenantKey && raw.vars) return raw as StoredBranding;
  } catch {
    // unreadable — treated as nothing stored
  }
  return null;
}

/** Removes every tenant colour from <html> and forgets the remembered copy. Safe to call at any time. */
export function clearAppliedBranding(): void {
  clearBrandLogo();
  const root = document.documentElement;
  for (const key of BRAND_VAR_KEYS) root.style.removeProperty(key);
  try {
    localStorage.removeItem(BRANDING_VARS_KEY);
  } catch {
    // storage unavailable
  }
}

/** Called on every route change: platform routes (and flag-off) get the platform brand; a stored copy from another scope is dropped. */
export function enforceBrandingScope(pathname: string): void {
  const scope = scopeForPath(pathname);
  if (!BRANDING_FLAGS.TENANT_BRANDING_ENABLED || scope === "PLATFORM") {
    clearAppliedBranding();
    return;
  }
  if (!BRANDING_FLAGS.STRICT_SCOPE_ISOLATION) return;
  const stored = readStoredBranding();
  if (stored && stored.scope !== scope) clearAppliedBranding();
}
